import { useCallback } from "react";
import {
  FlowCondition,
  FlowFieldOption,
  useFlows,
} from "../../../../../stores/flowsStore";

interface UseTriggerConditionsProps {
  conditions: FlowCondition[];
  conditionsLogic: string;
  onChange: (conditions: FlowCondition[], logic: string) => void;
}

export const useTriggerConditions = ({
  conditions,
  conditionsLogic,
  onChange,
}: UseTriggerConditionsProps) => {
  const { flowFields, operators, clients, custodians } = useFlows();

  const handleAddCondition = useCallback(() => {
    const newCondition: FlowCondition = {
      field: "",
      operator: "equals",
      value: "",
    };
    onChange([...conditions, newCondition], conditionsLogic);
  }, [conditions, conditionsLogic, onChange]);

  const handleRemoveCondition = useCallback(
    (index: number) => {
      const updated = conditions.filter((_, i) => i !== index);
      onChange(updated, conditionsLogic);
    },
    [conditions, conditionsLogic, onChange]
  );

  const handleConditionChange = useCallback(
    (index: number, field: keyof FlowCondition, value: string) => {
      const updated = conditions.map((condition, i) => {
        if (i === index) {
          const newCondition = { ...condition, [field]: value };
          // Reset value when field changes
          if (field === "field") {
            newCondition.value = "";
          }
          return newCondition;
        }
        return condition;
      });
      onChange(updated, conditionsLogic);
    },
    [conditions, conditionsLogic, onChange]
  );

  const handleLogicChange = useCallback(
    (logic: string) => {
      onChange(conditions, logic);
    },
    [conditions, onChange]
  );

  const getFieldOptions = useCallback(
    (fieldKey: string): FlowFieldOption[] => {
      const fieldDef = flowFields.find((f) => f.key === fieldKey);
      if (!fieldDef) return [];

      // Dynamic sources - use names as values for matching against transaction data
      if (fieldDef.source?.includes("clients")) {
        return clients.map((c) => ({
          value: c.client_name,
          label: c.client_name,
        }));
      }
      if (fieldDef.source?.includes("custodians")) {
        return custodians.map((c) => ({
          value: c.custodian_name,
          label: c.custodian_name,
        }));
      }

      return fieldDef.options || [];
    },
    [flowFields, clients, custodians]
  );

  const getFieldType = useCallback(
    (fieldKey: string): string => {
      const fieldDef = flowFields.find((f) => f.key === fieldKey);
      return fieldDef?.type || "text";
    },
    [flowFields]
  );

  return {
    flowFields,
    operators,
    handleAddCondition,
    handleRemoveCondition,
    handleConditionChange,
    handleLogicChange,
    getFieldOptions,
    getFieldType,
  };
};
